import { FunctionComponent } from "preact";
import { useEffect, useRef } from "preact/hooks";
import { forwardRef } from "preact/compat";
import { useConfig } from "./providers/ConfigProvider";
import { WidgetButton } from "./components/WidgetButton";
import { styled } from "goober";
import { ModeSelectionMenu } from "./components/ModeSelectionMenu";
import { ChatContent } from "./components/ChatContent";
import { VoiceContent } from "./components/VoiceContent";
import { useUnifiedWidget } from "./providers/UnifiedWidgetProvider";
import FocusTrap from "./FocusTrap";

interface UnifiedWidgetContainerProps {
  position: string;
  zIndex: number;
  textColor: string;
}

const UnifiedWidgetContainer = styled('div', forwardRef)(({ position, zIndex, textColor }: UnifiedWidgetContainerProps) => ({
  position: "fixed",
  display: "flex",
  flexDirection: "column",
  alignItems: position === "bottom-right" ? "flex-end" : "flex-start",
  bottom: '15px',
  right: position === "bottom-right" ? '15px' : undefined,
  left: position === "bottom-left" ? '15px' : undefined,
  fontSize: '16px',
  zIndex: zIndex,
  maxWidth: "calc(100% - 30px)",
  maxHeight: "calc(100% - 30px)",
  color: textColor,

  opacity: 1,
  animation: "fadeIn 0.2s ease-in-out none",
  "@keyframes fadeIn": {
    "0%": {
      opacity: 0,
      transform: "translateY(3px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(0)",
    }
  }
}));

const ContentContainer = styled('div')(({ width, height, open }: { width: number; height: number; open: boolean }) => ({
  position: "relative",
  width: `${width}px`,
  height: `${height}px`,
  maxWidth: open ? "100%" : "0",
  maxHeight: open ? "100%" : "0",
  pointerEvents: open ? "auto" : "none",
}));

export const UnifiedWidget: FunctionComponent = () => {
  const { config } = useConfig();
  const { updateState, open } = useUnifiedWidget();
  const widgetContainer = useRef<HTMLDivElement | null>(null);
  const widgetButton = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    function handleEscPress(e: KeyboardEvent) {
      if (e.key === 'Escape' && open) {
        e.preventDefault();
        e.stopPropagation();
        updateState({ open: false, mode: 'menu' });
      }
    }
    widgetContainer.current?.addEventListener('keydown', handleEscPress);
    return () => {
      widgetContainer.current?.removeEventListener('keydown', handleEscPress);
    }
  }, [widgetContainer, widgetButton, updateState, open]);

  const { position, zIndex, width, height, textColor } = config;

  return (
    <FocusTrap active={open}>
      <UnifiedWidgetContainer position={position} zIndex={zIndex} ref={widgetContainer} textColor={textColor}>
        <ContentContainer width={width} height={height} open={open}>
          {/* Mode selection menu - shown when mode is 'menu' */}
          <ModeSelectionMenu />
          
          {/* Chat content - shown when mode is 'chat' */}
          <ChatContent />
          
          {/* Voice content - shown when mode is 'voice' (renders in fixed position) */}
          <VoiceContent />
        </ContentContainer>
        
        {/* Widget button - always visible */}
        <WidgetButton ref={widgetButton} />
      </UnifiedWidgetContainer>
    </FocusTrap>
  )
}
